import { PostHv } from './helpers';
import { BlProfile, BrandliveEvent, Dictionary, RegistrationStepType, StepsCompleted, GlobalPrice, LanguagesAbbr } from '../types/working-model';

export enum EValidateRegistrationResponseType {
	valid = "valid",
	invalid = "invalid"
}

export function ValidateRegistration(eventUuid: string, registration_id: string, userCode: string | null, bl_token?: string, is_moderator?: boolean): Promise<IRegistrationResponse> {
	return PostHv(`/e3-validate-registration/${eventUuid}${is_moderator ? '?is_moderator=true' : ''}`, { registration_id, userCode }, bl_token);
}

interface Dict {
	[key: string]: any;
}

export interface IRegistrationResponse {
	registrationId: string;
	registered_language?: string; // Has to use underscore instead of carmel case since it is what is returned from microservices. 
	userVerified: boolean; // Email Link clicked
	token: string;
	validSessions: Array<any>,
	paidSessions: Array<any>;
	eventBundle: BrandliveEvent;
	playbackUrls: any;
	secondaryVideos: Dictionary;
	paymentRequired: boolean;
	userCode?: string; // Code generated when email link is clicked
	firesidesHost?: number[];
	userSavedSessions?: string[];
	authorizedPages: number[];
	stepsCompleted: StepsCompleted;
}

export interface IRegistrationRequest {
	fields: Dict;
	lang: string;
	source: string;
	timezone: string;
	isFinalStep: boolean;
	userCode?: string;
	utmParams?: Record<string, string>;
	referrer?: string;
	allQueryParams?: Record<string, string>;
	hostname?: string;
	action?: string;
	isFinalNonTicketStep: boolean;
	authCode?: string;
	redirect_uri?: string;
	step?: RegistrationStepType;
	registered_language: LanguagesAbbr;
}

export interface GoogleSSORequest {
	token: string;
	passcode?: string;
	lang: string;
	source: string;
	timezone: string;
	userCode?: string;
	utmParams?: string;
	referrer?: string;
}

export function Register(request: IRegistrationRequest, uuid: string, bl_token?: string, headers?: Record<string, string>, is_moderator?: boolean): Promise<IRegistrationResponse> {
	return PostHv(`/e3-create-registration/${uuid}${is_moderator ? '?is_moderator=true' : ''}`, { ...request }, bl_token, undefined, headers);
}

export function SSORegister(request: GoogleSSORequest, uuid: string, bl_token?: string): Promise<IRegistrationResponse> {
	return PostHv(`/e3-sso-registration/${uuid}`, { ...request }, bl_token);
}

export interface IProfileEditRequest {
	fields: Dict;
	channel: number;
	avatar: string | null;
}
export interface IProfileComplete extends IProfileEditRequest {
	lang: string;
	registrationId: string;
	eventUuid?: string;
	emailAddress?: string;
	source?: string;
	timezone?: string;
	step?: RegistrationStepType;
}

export interface IStripeOptions {
	channel_id: number,
	event_uuid: string,
	lang: string;
	profile: BlProfile | null,
	registration_uuid: string | null,
	source?: string;
	timezone?: string;
}

export interface IStripeData {
	[key: string]: string | boolean | GlobalPrice;
}

export function CompleteProfile(request: IProfileComplete, bl_token?: string): Promise<{ token: string; }> {
	return PostHv(`/e3-complete-profile`, { ...request }, bl_token);
}

export function EditProfile(profile: IProfileEditRequest, eventUuid: string, bl_token?: string): Promise<{ token: string; }> {
	return PostHv(`/e3-edit-profile`, { profile, eventUuid }, bl_token);
}

export function CreateStripeIntent(options: IStripeOptions, data: IStripeData): Promise<{ clientSecret: string; }> {
	return PostHv('/e3-create-intent', { options, data });
}
export interface IReRegistrationRequest {
	lang: string;
	timezone: string;
	eventUuid: string;
	email: string;
	action?: string; // for recaptcha v3
	hostname?: string; // for recaptcha v3
}

export function ReRegister({ lang, timezone, eventUuid, email, hostname, action }: IReRegistrationRequest, headers = {}): Promise<any> {
	return PostHv(
		`/e3-reregister/${eventUuid}`,
		{
			emailAddress: email,
			lang,
			timezone,
			source: window.location.href.replace('/registration', '').split(/\?|#/)[0],
			hostname,
			action
		},
		undefined,
		false,
		headers,
	);
}

export function ReRegisterCheck(eventUuid: string, code: string): Promise<IRegistrationResponse> {
	return PostHv(`/e3-reregister-check/${eventUuid}`, { code });
}

export function MagicLinkCheck(eventUuid: string, code: string): Promise<IRegistrationResponse> {
	return PostHv(`/e3-magiclink-check/${eventUuid}`, { code });
}

interface IValidateRegisteredUserSessions {
	eventUuid: string;
	blProfileToken: string;
	registrationId: string;
	checkType?: 'publish' | 'reload';
}

export function ValidateRegisteredUserSessions({ eventUuid, blProfileToken, registrationId, checkType }: IValidateRegisteredUserSessions): Promise<unknown> {
	return PostHv(
		`/e3-validate-registered-user-sessions/${eventUuid}`,
		{ registration_id: registrationId, checkType },
		blProfileToken,
	);
}